import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, ToggleLeft, HelpCircle, CheckCircle2, FileSpreadsheet, Zap } from 'lucide-react';

export default function CSVFeaturesDemo() {
  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'var(--color-bg-base)' }}>
      <div className="max-w-6xl mx-auto">
        <div
          className="p-4 mb-6 rounded"
          style={{
            background: 'var(--color-needs-human-tint)',
            borderLeft: '4px solid var(--color-brand)',
          }}
        >
          <div className="flex justify-between items-center">
            <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
              <strong>CSV IMPORT FEATURES DEMO:</strong> All 5 data import capabilities
            </p>
            <Link
              to="/demo-wizard"
              className="font-semibold py-2 px-4 rounded-lg transition-colors"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Try Full Wizard →
            </Link>
          </div>
        </div>

        <h1 className="cs-display mb-8 text-center" style={{ fontSize: '2.25rem' }}>
          Step 3: Financial Data Import – 5 Methods
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Feature 1: CSV Upload with Drag & Drop */}
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Upload className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
              <h2 className="cs-h2">1. CSV Upload – Drag & Drop</h2>
            </div>

            <div
              className="border-2 border-dashed rounded-lg p-8 text-center mb-4"
              style={{ borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}
            >
              <Upload className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--color-brand)' }} />
              <p className="cs-body font-semibold" style={{ fontSize: '1.125rem', color: 'var(--color-needs-human-text)' }}>Drag & drop CSV or click to upload</p>
              <p className="cs-meta mt-2">Parses 48 months automatically</p>
            </div>

            <div
              className="p-4 mb-4"
              style={{ background: 'transparent', borderLeft: '4px solid var(--color-verified)' }}
            >
              <CheckCircle2 className="h-5 w-5 inline mr-2" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>Features:</span>
              <ul className="mt-2 cs-meta space-y-1 ml-6" style={{ color: 'var(--color-verified)' }}>
                <li>✓ Drag & drop interface (react-dropzone)</li>
                <li>✓ Auto-parses 48 expense categories</li>
                <li>✓ Validates 12 months per year</li>
                <li>✓ Shows "48 months loaded" with checkmark</li>
                <li>✓ Real-time error: "Missing 2020 Apr revenue"</li>
              </ul>
            </div>

            <div className="rounded p-3" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-figure" style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <strong>Tech:</strong> PapaParse + react-dropzone
              </p>
            </div>
          </div>

          {/* Feature 2: Annual Summary Option */}
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <ToggleLeft className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
              <h2 className="cs-h2">2. Annual Summary Option</h2>
            </div>

            <div className="rounded-lg p-6 mb-4" style={{ background: 'var(--color-needs-human-tint)' }}>
              <div className="flex items-center gap-3 mb-4">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                  <ToggleLeft className="w-5 h-5" />
                  Toggle: "Enter yearly totals"
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block cs-label mb-2">Revenue 2019</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 font-semibold" style={{ color: 'var(--color-text-muted)' }}>$</span>
                    <input
                      type="text"
                      value="2,500,000"
                      readOnly
                      className="w-full pl-8 pr-4 py-2 rounded-lg"
                      style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    />
                  </div>
                </div>
                <p className="cs-meta font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                  Auto-expands to 12 equal months ($208,333/month)
                </p>
              </div>
            </div>

            <div
              className="p-4 mb-4"
              style={{ background: 'transparent', borderLeft: '4px solid var(--color-verified)' }}
            >
              <CheckCircle2 className="h-5 w-5 inline mr-2" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>Features:</span>
              <ul className="mt-2 cs-meta space-y-1 ml-6" style={{ color: 'var(--color-verified)' }}>
                <li>✓ Toggle between CSV and Annual mode</li>
                <li>✓ Input yearly total: $2,500,000</li>
                <li>✓ Auto-expands to 12 equal months</li>
                <li>✓ No CSV file needed</li>
                <li>✓ Perfect for simple businesses</li>
              </ul>
            </div>

            <div className="rounded p-3" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-figure" style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <strong>Code:</strong> {'{isAnnualMode && <AnnualInput />}'}
              </p>
            </div>
          </div>

          {/* Feature 3: Download Sample CSV */}
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-8 h-8" style={{ color: 'var(--color-verified)' }} />
              <h2 className="cs-h2">3. Download Sample CSV</h2>
            </div>

            <div className="rounded-lg p-6 mb-4" style={{ background: 'transparent' }}>
              <p className="cs-meta font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                Click "Download Template" – Get pre-filled CSV for:
              </p>

              <div className="flex flex-col gap-2">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-verified)' }}>
                  <span className="text-2xl">🍽️</span>
                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Restaurant Template</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-brand)' }}>
                  <span className="text-2xl">🏖️</span>
                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Tourism Template</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-brand)' }}>
                  <span className="text-2xl">🦞</span>
                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Seafood Template</span>
                </button>
              </div>
            </div>

            <div
              className="p-4 mb-4"
              style={{ background: 'transparent', borderLeft: '4px solid var(--color-verified)' }}
            >
              <CheckCircle2 className="h-5 w-5 inline mr-2" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>Includes:</span>
              <ul className="mt-2 cs-meta space-y-1 ml-6" style={{ color: 'var(--color-verified)' }}>
                <li>✓ 48 months of sample data</li>
                <li>✓ All 48 expense columns</li>
                <li>✓ Industry-specific values</li>
                <li>✓ Ready to edit & re-upload</li>
              </ul>
            </div>

            <div className="rounded p-3" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-figure" style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <strong>Link:</strong> {'<a href="/templates/restaurant.csv" download>'}
              </p>
            </div>
          </div>

          {/* Feature 4: Guided Import - Tooltips */}
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
              <h2 className="cs-h2">4. Guided Import – Tooltips</h2>
            </div>

            <div className="rounded-lg p-6 mb-4" style={{ background: 'var(--color-needs-human-tint)' }}>
              <p className="cs-meta font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>Live Examples:</p>

              <div className="space-y-3">
                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>COGS - Variable</span>
                    <HelpCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <p className="cs-meta p-2 rounded" style={{ fontSize: '0.75rem', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                    Tooltip: "COGS-V = food cost, NOT rent"
                  </p>
                </div>

                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
                  <span className="cs-meta font-semibold" style={{ fontSize: '0.75rem', color: 'var(--color-needs-human-text)' }}>❌ Validation Error</span>
                  <p className="cs-body mt-1" style={{ color: 'var(--color-needs-human-text)' }}>"2019 Jan revenue missing"</p>
                  <div className="mt-2 h-2 rounded" style={{ background: 'var(--color-needs-human-tint)' }}></div>
                </div>

                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-verified)' }}>
                  <span className="cs-meta font-semibold" style={{ fontSize: '0.75rem', color: 'var(--color-verified)' }}>Valid Data</span>
                  <p className="cs-body mt-1" style={{ color: 'var(--color-verified)' }}>Revenue: $150,000</p>
                  <div className="mt-2 h-2 rounded" style={{ background: 'transparent' }}></div>
                </div>
              </div>
            </div>

            <div
              className="p-4 mb-4"
              style={{ background: 'transparent', borderLeft: '4px solid var(--color-verified)' }}
            >
              <CheckCircle2 className="h-5 w-5 inline mr-2" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>Features:</span>
              <ul className="mt-2 cs-meta space-y-1 ml-6" style={{ color: 'var(--color-verified)' }}>
                <li>✓ Tooltips explain each field</li>
                <li>✓ Real-time validation messages</li>
                <li>✓ Red border on bad input</li>
                <li>✓ Green check when fixed</li>
              </ul>
            </div>

            <div className="rounded p-3" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-figure" style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <strong>Code:</strong> {'<Tooltip content="Variable cost only">'}
              </p>
            </div>
          </div>
        </div>

        {/* Feature 5: Bulk Upload API */}
        <div className="rounded-xl p-8 mb-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <FileSpreadsheet className="w-10 h-10" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-display" style={{ fontSize: '1.875rem' }}>5. Bulk Upload API – 100+ Claims</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="cs-h3 font-semibold mb-3">Live Endpoint</h3>
              <div className="p-4 rounded-lg cs-figure mb-4" style={{ fontSize: '0.8125rem', background: 'var(--color-bg-base)', color: 'var(--color-verified)' }}>
                <p className="mb-2"><span style={{ color: 'var(--color-needs-human-text)' }}>POST</span> /api/v1/bulk/upload</p>
                <p className="mb-2">Content-Type: multipart/form-data</p>
                <p>file: bulk_claims.csv</p>
              </div>

              <h3 className="cs-h3 font-semibold mb-3">Response</h3>
              <div className="p-4 rounded-lg cs-figure" style={{ fontSize: '0.75rem', background: 'var(--color-bg-base)', color: 'var(--color-verified)' }}>
                <pre>{`{
  "processed": 87,
  "failed": 3,
  "reports": [
    "RPT-001",
    "RPT-002",
    ...
  ],
  "errors": [
    "Row 12: Missing revenue",
    "Row 45: Invalid zone"
  ]
}`}</pre>
              </div>
            </div>

            <div>
              <h3 className="cs-h3 font-semibold mb-3">Features</h3>
              <div
                className="p-4 mb-4"
                style={{ background: 'transparent', borderLeft: '4px solid var(--color-verified)' }}
              >
                <ul className="cs-meta space-y-2" style={{ color: 'var(--color-verified)' }}>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Batch Processing:</strong> Upload 100+ claims in one CSV</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Quota Enforcement:</strong> Checks org limits before processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Async PDF Generation:</strong> All reports queued via Celery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>Error Tracking:</strong> Returns failed rows with reasons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span><strong>JWT Auth Required:</strong> Secure multi-tenant access</span>
                  </li>
                </ul>
              </div>

              <div className="p-4" style={{ background: 'var(--color-needs-human-tint)', borderLeft: '4px solid var(--color-brand)' }}>
                <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                  <strong>Performance:</strong> Processes 100 claims in ~45 seconds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User Flow Summary */}
        <div className="rounded-xl p-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <h2 className="cs-display mb-6 text-center flex items-center justify-center gap-2" style={{ fontSize: '1.875rem' }}><Zap className="w-6 h-6" aria-hidden="true" /> USER FLOW – 5 MINUTES TO $450K CLAIM</h2>

          <div className="grid grid-cols-6 gap-4">
            {[
              { step: 1, title: "Enter name", icon: "👤" },
              { step: 2, title: "Pick Zone A + Restaurant", icon: "🏢" },
              { step: 3, title: "Drag CSV OR enter $2.5M/year", icon: "📊" },
              { step: 4, title: "See payroll auto-filled", icon: "💰" },
              { step: 5, title: "Review $450k estimate", icon: "🎯" },
              { step: 6, title: "Click Generate → PDF in 8s", icon: "📄" }
            ].map((item) => (
              <div key={item.step} className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="cs-figure-lg mb-1" style={{ fontSize: '1.5rem' }}>Step {item.step}</div>
                <div className="cs-meta">{item.title}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="cs-h2 font-bold" style={{ fontSize: '1.5rem' }}>No training needed. Zero learning curve.</p>
            <p className="cs-body mt-2" style={{ fontSize: '1.125rem' }}>From data upload to finalized PDF report in under 5 minutes.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to="/demo-wizard"
            className="inline-block font-bold py-4 px-8 rounded-lg transition-colors text-lg"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            Try the Full Wizard Now →
          </Link>
        </div>
      </div>
    </div>
  );
}
